// [2026-09-02] ADDED — the 3am screen: which dependency is down, and what this environment is
// actually running with.
//
// WHY THE FLAGS MATTER AS MUCH AS THE PING. Half of "the product is broken" turns out to be a flag:
// billing off so nothing is refused, runtime clients off so onboarding fails, auth off so a key is
// not checked. Reading them from the running service beats reading them from a deployment YAML that
// may not be what is deployed.
//
// SECRETS ARE SHOWN AS PRESENT / MISSING, NEVER AS VALUES. `secrets_configured` returns booleans by
// design, and this screen must not become the one place a secret is rendered in a browser.

import {
  Card, Notice, Skeleton, useAsync,
} from '@incruiter/proctoring-client-frontend'
import { Badge } from '@incruiter/proctoring-client-frontend/src/components/ui/badge.jsx'
import { admin } from '../api.js'

const Row = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '230px 1fr', gap: 10,
                padding: '6px 0', fontSize: 13 }}>
    <span className="muted">{label}</span>
    <span>{children}</span>
  </div>
)

const Bool = ({ value, onLabel = 'on', offLabel = 'off', goodWhenOff = false }) => (
  <Badge variant={value ? (goodWhenOff ? 'degraded' : 'healthy')
                        : (goodWhenOff ? 'healthy' : 'idle')}>
    {value ? onLabel : offLabel}
  </Badge>
)

export default function Diagnostics({ onError }) {
  const { data, error, loading } = useAsync(() => admin.health(), [])

  if (error) { onError?.(error); return <Notice kind="error">{error.message}</Notice> }
  if (loading || !data) return <Skeleton height={260} />

  const s = data.storage || {}
  const svc = data.service || {}
  const f = data.features || {}
  const secrets = data.secrets_configured || {}
  const limits = data.limits || {}

  const storageDown = (s.mongo_configured && !s.mongo_reachable)
    || (s.redis_configured && !s.redis_reachable)

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {storageDown && (
        <Notice kind="error">
          A configured dependency is NOT reachable. Writes may be failing right now.
        </Notice>
      )}

      <div style={{ display: 'grid', gap: 16,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
        <Card title="Storage" subtitle="Configured, and actually reachable">
          <Row label="Backend"><span className="mono">{s.backend}</span></Row>
          <Row label="Mongo configured"><Bool value={s.mongo_configured} onLabel="yes" offLabel="no" /></Row>
          <Row label="Mongo reachable">
            <Badge variant={s.mongo_reachable ? 'healthy' : (s.mongo_configured ? 'critical' : 'idle')}>
              {s.mongo_reachable ? 'reachable' : (s.mongo_configured ? 'UNREACHABLE' : 'n/a')}
            </Badge>
          </Row>
          <Row label="Redis configured"><Bool value={s.redis_configured} onLabel="yes" offLabel="no" /></Row>
          <Row label="Redis reachable">
            <Badge variant={s.redis_reachable ? 'healthy' : (s.redis_configured ? 'critical' : 'idle')}>
              {s.redis_reachable ? 'reachable' : (s.redis_configured ? 'UNREACHABLE' : 'n/a')}
            </Badge>
          </Row>
        </Card>

        <Card title="Service" subtitle="What this process believes it is">
          {Object.entries(svc).map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, ' ')}><span className="mono">{String(v)}</span></Row>
          ))}
        </Card>

        <Card title="Features" subtitle="The flags this environment is running with">
          <Row label="SDK enabled"><Bool value={f.sdk_enabled} /></Row>
          {/* AUTH OFF IS A RED FLAG, not a neutral one — it means API keys are not being checked. */}
          <Row label="API-key auth">
            <Badge variant={f.auth_enabled ? 'healthy' : 'critical'}>
              {f.auth_enabled ? 'enforced' : 'DISABLED'}
            </Badge>
          </Row>
          <Row label="Credits enabled"><Bool value={f.credits_enabled} /></Row>
          <Row label="SDK billing"><Bool value={f.sdk_billing_enabled} /></Row>
          <Row label="Runtime tenants"><Bool value={f.runtime_clients_enabled} /></Row>
          <Row label="Runtime API keys"><Bool value={f.runtime_api_keys_enabled} /></Row>
          <Row label="Staff accounts"><Bool value={f.admin_users_enabled} /></Row>
          <Row label="Rate limiting"><Bool value={f.rate_limit_enabled} /></Row>
        </Card>

        <Card title="Secrets" subtitle="Present or missing — never the value">
          {Object.entries(secrets).map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, ' ')}>
              <Badge variant={v ? 'healthy' : 'critical'}>{v ? 'configured' : 'MISSING'}</Badge>
            </Row>
          ))}
        </Card>

        <Card title="Limits" subtitle="TTLs and retention this deployment enforces">
          {Object.entries(limits).map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, ' ')}><span className="mono">{String(v)}</span></Row>
          ))}
        </Card>
      </div>
    </div>
  )
}
